<template>
  <template v-if="isCancelled">
    <WdsTooltip left>
      <WdsButtonGhost disabled>
        <WdsIcon name="overflow-menu-horizontal" />
      </WdsButtonGhost>
      <template #tooltip-content>
        {{ $t('This contract has been cancelled. No actions available.') }}
      </template>
    </WdsTooltip>
  </template>

  <WdsMenu v-else-if="managesWorkers" placement="bottom-end" z-index="10" global>
    <template #trigger>
      <WdsButtonGhost class="tw-float-right" data-test="candidate-menu-trigger">
        <WdsIcon name="overflow-menu-horizontal" />
      </WdsButtonGhost>
    </template>

    <WdsMenuOption v-if="!contractAccepted" :href="acceptContractUrl">
      <WdsIcon name="draw-signature" :size="16" />
      <span class="tw-whitespace-nowrap">{{ $t('Review contract') }}</span>
    </WdsMenuOption>

    <WdsMenuOption :href="hireViewUrl">
      <WdsIcon name="hire" :size="16" />
      <span class="tw-whitespace-nowrap">{{ $t('Contract overview') }}</span>
    </WdsMenuOption>

    <WdsMenuOption
      v-if="contract"
      :disabled="!contract.pdfUrl"
      :href="contract.pdfUrl"
      target="_blank"
    >
      <WdsIcon name="download" :size="16" weight="thick" />
      <span class="tw-whitespace-nowrap">{{ $t('Download contract PDF') }}</span>
    </WdsMenuOption>

    <WdsMenuOption v-if="conversation" :href="conversation.url">
      <WdsIcon name="chat" :size="16" weight="thick" />
      <span class="tw-whitespace-nowrap">{{ $t('Go to conversation') }}</span>
    </WdsMenuOption>
  </WdsMenu>

  <DeclineContractModal
    :open="showDeclineContractModal"
    :hire-id="hireId"
    :account-id="recruiterId"
    @close="showDeclineContractModal = false"
    @hire-canceled="$emit('hire-canceled')"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsTooltip,
} from '@worksome/wds-components'

import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import DeclineContractModal from '@/components/Hire/DeclineContractModal.vue'
import { HireActiveStatus } from '@/gql/graphql'

type Contract = {
  id: string
  status: string
  pdfUrl?: string
  workerAcceptedAt?: string
}

type Conversation = {
  url: string
}

const props = defineProps<{
  hireId: string
  recruiterId: string
  status: string
  managesWorkers: boolean
  contract: Contract
  conversation: Conversation | null
}>()

defineEmits(['hire-canceled'])

const showDeclineContractModal = ref(false)

const hireViewUrl = computed(() =>
  laravelNamedRoute(namedRoutes.hires.hire.view, { hire: props.hireId, tab: 'overview' }),
)

const contractAccepted = computed(() => props.contract?.workerAcceptedAt)

const isCancelled = computed(() => props.status === HireActiveStatus.Cancelled)

const acceptContractUrl = computed(
  () =>
    `${laravelNamedRoute(namedRoutes.hires.hire.view, { hire: props.hireId, tab: 'overview' })}?acceptContract=1`,
)
</script>
